// Mirrors the dashboard_backend onboarding app (apps/onboarding) contract.

import { type CompanyType, UseCase } from 'api/types';

// Onboarding's client type is the company's account type — reuse the api type instead of redefining.
export type ClientType = CompanyType;

export type StepStatus = 'pending' | 'complete' | 'skipped';

export type CompletionType = 'auto' | 'manual';

/** Whose action settles a step: `user` is tracked per member, `company` once for the whole company. */
export type ConditionLevel = 'user' | 'company';

export type OnboardingLayout = 'window' | 'docked';

export type OnboardingStateResponse = {
  wizard_complete: boolean;
  /**
   * Whether this membership is enrolled in onboarding. False for anyone who joined before the
   * checklist shipped — they get the assistant chat without the wizard or checklist. Absent on an
   * older backend, which callers read as enabled.
   */
  checklist_enabled?: boolean;
  layout?: OnboardingLayout;
  id?: string;
  company_id?: string;
  client_type?: ClientType;
  platforms?: string[];
  verticals?: UseCase[];
  completed_steps?: number;
  total_steps?: number;
  created_at?: string;
  updated_at?: string;
};

export type SetupRequest = {
  client_type: ClientType;
  platforms?: string[];
  verticals?: UseCase[];
};

export type ChecklistStep = {
  step_id: string;
  section: string;
  title: string;
  description: string;
  link: string | null;
  docs_url: string | null;
  video_url: string | null;
  skippable: boolean;
  condition_level: ConditionLevel;
  status: StepStatus;
  completion_type: CompletionType | null;
  /** Name or email of the teammate who settled a company-level step; null for user-level steps. */
  completed_by: string | null;
};

export type ChecklistSection = {
  section_id: string;
  section_label: string;
  steps: ChecklistStep[];
};

export type ChecklistDisplayMode = 'full' | 'narrative';

export type ChecklistResponse = {
  sections: ChecklistSection[];
  completed_count: number;
  total_count: number;
  display_mode: ChecklistDisplayMode;
};

export type SyncResponse = {
  updated_steps: Array<{ step_id: string; status: string; completion_type: string }>;
};

// Uses Core's use_case values directly (the single source of truth); labels are just presentation.
export const VERTICAL_OPTIONS: Array<{ value: UseCase; label: string; description: string }> = [
  {
    value: UseCase.MORTGAGE_LENDING,
    label: 'Mortgage Lending',
    description: 'Income, employment, and asset verification for mortgages.',
  },
  {
    value: UseCase.HOME_EQUITY_LENDING,
    label: 'Home Equity Lending',
    description: 'Income and asset verification for home equity products.',
  },
  {
    value: UseCase.CONSUMER_LENDING,
    label: 'Consumer Lending',
    description: 'Income and bank verification for consumer credit.',
  },
  {
    value: UseCase.AUTO_LENDING,
    label: 'Auto Lending',
    description: 'Banking, paystub, and insurance verification for auto loans.',
  },
  {
    value: UseCase.TENANT_SCREENING,
    label: 'Tenant Screening',
    description: 'Income verification and fraud detection for rentals.',
  },
  {
    value: UseCase.BACKGROUND_SCREENING,
    label: 'Background Screening',
    description: 'Employment history verification for background checks.',
  },
  {
    value: UseCase.BANKING,
    label: 'Banking',
    description: 'Income and account verification for banking products.',
  },
  {
    value: UseCase.GOVERNMENT,
    label: 'Government',
    description: 'Wage and self-employment verification for government programs.',
  },
];
